import {Device} from '@webmate-web-core/shared/types/devices/device';
import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {AdvertisedDeployable, Deployable} from '@webmate-web-core/shared/types/devices/device-template';
import {Injectable} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {isDefined} from '@webmate-web-core/shared/utils/utils';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {AuthInterceptorSkipHeader, BaseUrlSkipHeader} from '@webmate-web-core/shared/constants/interceptor-custom-headers.constants';
import {Browser} from '@webmate-web-core/shared/types/browser';
import {Platform, PlatformType} from '../types/platform';

@Injectable({
    providedIn: 'root'
})
export class LogoResolverService {

    private static readonly DEFAULT_ENTRY = 'default';
    private static ASSET_IMG_BASE_PATH: string;
    private static ASSET_MODELS_PATH: string;
    private static ASSET_PLATFORM_PATH: string;
    private static ASSET_BROWSER_PATH: string;

    private _logoForPlatform: {[s: string]: Observable<string>} = {};
    private _logoForBrowser: {[s: string]: Observable<string>} = {};

    private assetLogosModelConfig$: ReplaySubject<any>;
    private assetsLogosPlatformConfig$: ReplaySubject<any>;
    private assetsLogosBrowserConfig$: ReplaySubject<any>;

    private static prepareLookupString(s: string | undefined): string {
        return s ? s.toLowerCase() : 'undefined';
    }

    private getUnauthorizedForeignUrlHeaders() {
        return new HttpHeaders().set(AuthInterceptorSkipHeader, '').set(BaseUrlSkipHeader, '');
    }

    constructor(private http: HttpClient, private webmateOptions: WebmateOptions) {
        LogoResolverService.ASSET_IMG_BASE_PATH = webmateOptions.assetBaseUri;
        LogoResolverService.ASSET_MODELS_PATH = LogoResolverService.ASSET_IMG_BASE_PATH + '/models';
        LogoResolverService.ASSET_PLATFORM_PATH = LogoResolverService.ASSET_IMG_BASE_PATH + '/platform';
        LogoResolverService.ASSET_BROWSER_PATH = LogoResolverService.ASSET_IMG_BASE_PATH + '/browsers';
    }

    /**
     * Try to resolve the desired platformType and version. Always try to resolve with the highest specification. If not found
     * then try to resolve one hierarchy less, e.g. platformType: 'iOS' version: '14.1.1'.
     * If '14.1.1' is not found then try to return the 'iOS' asset path. If 'iOS' is not found then return
     * the default icon for platforms. In general, there should be a default icon for every platformType.
     */
    private getPlatformLogoAssetPath(platformType: string, version: string): Observable<string> {
        if (!this.assetsLogosPlatformConfig$) {
            this.assetsLogosPlatformConfig$ = new ReplaySubject<any>(1);
            this.http.get<any>(LogoResolverService.ASSET_PLATFORM_PATH + '/platforms.json', {headers: this.getUnauthorizedForeignUrlHeaders()}).pipe(take(1)).subscribe(models => {
                this.assetsLogosPlatformConfig$.next(models);
            });
        }
        return this.assetsLogosPlatformConfig$.asObservable().pipe(map(platformConfig => {
            platformType = LogoResolverService.prepareLookupString(platformType);
            version = LogoResolverService.prepareLookupString(version);
            let platformLookup = platformConfig.platforms[platformType];
            if (isDefined(platformLookup)) {
                let versionLookup = platformLookup[version];
                if (isDefined(versionLookup)) {
                    return `${LogoResolverService.ASSET_PLATFORM_PATH}/${platformType}/${versionLookup}`;
                } else {
                    let platformDefaultLookup = platformLookup[LogoResolverService.DEFAULT_ENTRY];
                    if (isDefined(platformDefaultLookup)) {
                        return `${LogoResolverService.ASSET_PLATFORM_PATH}/${platformType}/${platformDefaultLookup}`;
                    } else {
                        return `${LogoResolverService.ASSET_PLATFORM_PATH}/${platformConfig.platforms[LogoResolverService.DEFAULT_ENTRY]}`;
                    }
                }
            } else {
                return `${LogoResolverService.ASSET_PLATFORM_PATH}/${platformConfig.platforms[LogoResolverService.DEFAULT_ENTRY]}`;
            }
        }))
    }

    /**
     * Try to resolve the desired manufactured and model. Always try to resolve with the highest specification. If not found
     * then try to resolve one hierarchy less, e.g. manufacturerName: 'Samsung' modelName: 'Galaxy Tab S6'.
     * If 'Galaxy Tab S6' is not found then try to return the 'Samsung' asset path. If 'Samsung' is not found then return
     * the default icon for models. In general, there should be a default icon for every manufacturer.
     */
    private getModelNameLogoAssetPath(manufacturer: string, model: string): Observable<string> {
        if (!this.assetLogosModelConfig$) {
            this.assetLogosModelConfig$ = new ReplaySubject<any>(1);
            this.http.get<any>(LogoResolverService.ASSET_MODELS_PATH + '/models.json', {headers: this.getUnauthorizedForeignUrlHeaders()}).pipe(take(1)).subscribe(models => {
                this.assetLogosModelConfig$.next(models);
            });
        }
        return this.assetLogosModelConfig$.asObservable().pipe(map(modelConfig => {
            manufacturer = LogoResolverService.prepareLookupString(manufacturer);
            model = LogoResolverService.prepareLookupString(model);
            let manufacturerLookup = modelConfig.models[manufacturer];
            if (isDefined(manufacturerLookup)) {
                let modelLookup = manufacturerLookup[model];
                if (isDefined(modelLookup)) {
                    return `${LogoResolverService.ASSET_MODELS_PATH}/${manufacturer}/${modelLookup}`;
                } else {
                    let manufacturerDefaultLookup = manufacturerLookup[LogoResolverService.DEFAULT_ENTRY];
                    if (isDefined(manufacturerDefaultLookup)) {
                        return `${LogoResolverService.ASSET_MODELS_PATH}/${manufacturer}/${manufacturerDefaultLookup}`;
                    } else {
                        return `${LogoResolverService.ASSET_MODELS_PATH}/${modelConfig.models[LogoResolverService.DEFAULT_ENTRY]}`;
                    }
                }
            } else {
                // Some components like BrowserChooserComponent only pass the model
                // remove the default entry from the lookup map, as it's formatted differently
                let manufacturers = Object.keys(modelConfig.models).filter(x => x !== 'default');
                for (let manufacturerTmp of manufacturers) {
                    let modelLookup = modelConfig.models[manufacturerTmp][model];
                    if (isDefined(modelLookup)) {
                        return `${LogoResolverService.ASSET_MODELS_PATH}/${manufacturerTmp}/${modelLookup}`;
                    }
                }
                return `${LogoResolverService.ASSET_MODELS_PATH}/${modelConfig.models[LogoResolverService.DEFAULT_ENTRY]}`;
            }
        }))

    }

    private getBrowserLogoAssetPath(browserType: string, version: string): Observable<string> {
        if (!this.assetsLogosBrowserConfig$) {
            this.assetsLogosBrowserConfig$ = new ReplaySubject<any>(1);
            this.http.get<any>(LogoResolverService.ASSET_BROWSER_PATH + '/browsers.json', {headers: this.getUnauthorizedForeignUrlHeaders()}).pipe(take(1)).subscribe(browsers => {
                this.assetsLogosBrowserConfig$.next(browsers);
            });
        }
        return this.assetsLogosBrowserConfig$.asObservable().pipe(map(browserConfig => {
            browserType = LogoResolverService.prepareLookupString(browserType);
            version = LogoResolverService.prepareLookupString(version);
            let browserLookup = browserConfig.browsers[browserType];
            if (isDefined(browserLookup)) {
                let versionLookup = browserLookup[version];
                if (isDefined(versionLookup)) {
                    return `${LogoResolverService.ASSET_BROWSER_PATH}/${browserType}/${versionLookup}`;
                } else {
                    let browserDefaultLookup = browserLookup[LogoResolverService.DEFAULT_ENTRY];
                    if (isDefined(browserDefaultLookup)) {
                        return `${LogoResolverService.ASSET_BROWSER_PATH}/${browserType}/${browserDefaultLookup}`;
                    } else {
                        return `${LogoResolverService.ASSET_BROWSER_PATH}/${browserConfig.browsers[LogoResolverService.DEFAULT_ENTRY]}`;
                    }
                }
            } else {
                return `${LogoResolverService.ASSET_BROWSER_PATH}/${browserConfig.browsers[LogoResolverService.DEFAULT_ENTRY]}`;
            }
        }))
    }

    getLogoForDevice(device: Device): Observable<string> {
        let model = (device.properties)[DevicePropertyNames.ModelName];

        if (model) {
            return this.getLogoForModel(device.properties[DevicePropertyNames.ManufacturerName], model);
        } else {
            return this.getLogoForPlatform(device.platform);
        }
    }

    getLogoForModel(manufacturerName: string, modelName: string): Observable<string> {
        let id = `${manufacturerName} ${modelName}`;
        if (this._logoForPlatform[id] === undefined) {
            let modelNameLogoAssetPath = this.getModelNameLogoAssetPath(manufacturerName, modelName);
            this._logoForPlatform[id] = modelNameLogoAssetPath;
        }
        return this._logoForPlatform[id];
    }

    getLogoForPlatform(platform: Platform): Observable<string> {
        let id = platform?.getFullName();

        if (this._logoForPlatform[id] === undefined) {
            this._logoForPlatform[id] = this.getPlatformLogoAssetPath(platform.platformType, platform.platformVersion);
        }
        return this._logoForPlatform[id];
    }

    getGenericLogoForPlatformType(platformType: string): Observable<string> {
        // TODO caching
        return this.getPlatformLogoAssetPath(platformType, undefined);
    }

    getLogosForDeployables(deployables: Deployable[]): Observable<AdvertisedDeployable[]> {
        let uniqueDeployables: Deployable[] = [];

        deployables.forEach(deployable => {
            if (!uniqueDeployables.find(d => d.platform.equals(deployable.platform) && (d.modelName === deployable.modelName)
                && (!deployable.properties[DevicePropertyNames.OpenSTFSerial] || d.properties[DevicePropertyNames.OpenSTFSerial] === deployable.properties[DevicePropertyNames.OpenSTFSerial]))) {
                uniqueDeployables.push(deployable);
            }
        });

        if (uniqueDeployables.length === 0) {
            return of([]);
        }

        return combineLatest(uniqueDeployables.map(d => {
            return this.getLogoForDeployable(d);
        }));
    }

    getLogoForDeployable(d: Deployable) {
        if (d.modelName) {
            let platformLogo = this.getLogoForPlatform(d.platform);
            let modelLogo = this.getLogoForModel(d.properties[DevicePropertyNames.ManufacturerName], d.modelName);
            return combineLatest([modelLogo, platformLogo]).pipe(map(logos => new AdvertisedDeployable(d.modelName, logos[0], logos[1])));
        } else {
            return this.getLogoForPlatform(d.platform).pipe(map(logo => new AdvertisedDeployable(d.platform.getFullName(), logo)));
        }
    }

    getLogoForBrowser(browser: Browser): Observable<string> {
        let id = browser.browserType+"-"+browser.version; // modify for speed-precision trade-off
        if (this._logoForBrowser[id] === undefined) {
            this._logoForBrowser[id] = this.getBrowserLogoAssetPath(browser.browserType, browser.version);
        }
        return this._logoForBrowser[id];
    }

    getGenericLogoForBrowserType(browserType: string) {
        // TODO caching
        return this.getBrowserLogoAssetPath(browserType, undefined);
    }

}
